import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ModulesContext from "../context/ModulesContext";
import {
  isModulePermitted,
  resolveDefaultModulePath,
} from "../util/defaultModule";

/**
 * Mobile bottom tab bar — the four destinations that carry the day-to-day work
 * on a phone. Shown below the `lg` breakpoint only; desktop keeps the 256px
 * sidebar untouched. Everything that is not one of these four is still reached
 * through the hamburger drawer in the header.
 */

const ACTIVE_COLOR = "#0F7679";
const INACTIVE_COLOR = "#6A7878";

type IconProps = { color: string };

const IconHome = ({ color }: IconProps) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 20 20"
    fill="none"
    stroke={color}
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 9.5L10 4l7 5.5V17H3z" />
    <path d="M8 17v-5h4v5" />
  </svg>
);

const IconItems = ({ color }: IconProps) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 20 20"
    fill="none"
    stroke={color}
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 7l7-4 7 4v9l-7 4-7-4z" />
    <path d="M3 7l7 4 7-4M10 11v9" />
  </svg>
);

const IconModifiers = ({ color }: IconProps) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 20 20"
    fill="none"
    stroke={color}
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 6h14M3 10h14M3 14h14" />
    <circle cx="7" cy="6" r="1.6" fill="#FFFFFF" />
    <circle cx="13" cy="10" r="1.6" fill="#FFFFFF" />
    <circle cx="8.5" cy="14" r="1.6" fill="#FFFFFF" />
  </svg>
);

const IconSales = ({ color }: IconProps) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 20 20"
    fill="none"
    stroke={color}
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 3h10v14l-2-1.5L11 17l-2-1.5L7 17l-2-1.5z" />
    <path d="M7.5 7h5M7.5 10h5" />
  </svg>
);

type Tab = {
  key: string;
  /** Label shown under the icon, and the translation key for it. */
  label: string;
  path: string;
  icon: ({ color }: IconProps) => JSX.Element;
  /** Permission name required to see the tab; Home is always available. */
  module?: string;
  /** Sidebar title, kept in localStorage the way the sidebar does on click. */
  title?: string;
};

const BottomTabBar = () => {
  const nav = useNavigate();
  const location = useLocation();
  const { modules, isLoading } = useContext(ModulesContext);
  // LanguageContext has no provider in this app; the shell's working source
  // of translations is window.languageContext, which Header fills in
  // asynchronously and announces with a "languageLoaded" DOM event.
  const [language, setLanguage] = useState<any>(
    () => window.languageContext?.language
  );
  useEffect(() => {
    const refresh = () => setLanguage(window.languageContext?.language);
    refresh();
    document.addEventListener("languageLoaded", refresh);
    return () => document.removeEventListener("languageLoaded", refresh);
  }, []);

  const t = (key: string): string => language?.[key] ?? key;

  const homePath = resolveDefaultModulePath(modules);

  const tabs: Tab[] = [
    { key: "home", label: "Home", path: homePath, icon: IconHome },
    {
      key: "items",
      label: "Items",
      path: "/items",
      icon: IconItems,
      module: "Items",
      title: "Items",
    },
    {
      key: "modifiers",
      label: "Modifiers",
      path: "/productModifiers",
      icon: IconModifiers,
      module: "Product Modifiers",
      title: "Product Modifiers",
    },
    {
      key: "sales",
      label: "Sales",
      path: "/reporting/sales-overview",
      icon: IconSales,
      module: "Recent Sales",
      title: "Sales Overview",
    },
  ];

  const visibleTabs = tabs.filter(
    (tab) => !tab.module || isModulePermitted(modules, tab.module)
  );

  // Until the permissions are known every destination is filtered out and
  // Home resolves blindly to /dashboard — don't paint a one-tab bar that
  // snaps to four (and can 403) a moment later. Only the FIRST load is
  // gated: a sync / company switch re-runs getAccountInfor with the old
  // modules still in hand, and the bar must not blink out for that.
  if (isLoading && !(modules && modules.length)) {
    return null;
  }

  // Home exists to reach the user's default module. When that resolves onto
  // a tab that is already on the bar (an Items-only role, say), a second
  // tab for the same route is noise — drop Home rather than duplicate it.
  const barTabs = visibleTabs.filter(
    (tab) =>
      tab.key !== "home" ||
      !visibleTabs.some((other) => other.key !== "home" && other.path === homePath)
  );

  const pathWithoutLang = location.pathname.replace(/^\/[a-z]{2}\//, "/");
  const matches = (path: string) =>
    pathWithoutLang === path || pathWithoutLang.startsWith(path + "/");

  // A destination tab wins over Home, so that a Home that resolves to /items
  // doesn't light up two tabs at once.
  const activeKey =
    barTabs.find((tab) => tab.key !== "home" && matches(tab.path))?.key ??
    (matches(homePath) ? "home" : "");

  const handleTabClick = (tab: Tab) => {
    const lang =
      location.pathname.split("/")[1] ||
      localStorage.getItem("language") ||
      "en";
    if (tab.title) {
      localStorage.setItem("title", tab.title);
    }
    nav(`/${lang}${tab.path}`);
  };

  return (
    <nav
      aria-label={t("Menu")}
      className="finex-tabbar lg:hidden fixed bottom-0 left-0 right-0 z-30 flex bg-white border-t border-t-[#EBF0F0] font-inter"
    >
      {barTabs.map((tab) => {
        const isActive = activeKey === tab.key;
        const color = isActive ? ACTIVE_COLOR : INACTIVE_COLOR;
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => handleTabClick(tab)}
            aria-current={isActive ? "page" : undefined}
            className="flex-1 flex flex-col items-center justify-center gap-[3px] min-h-[44px] focus:outline-none"
          >
            <tab.icon color={color} />
            <span
              className="text-[11px] font-medium leading-4"
              style={{ color }}
            >
              {t(tab.label)}
            </span>
          </button>
        );
      })}
    </nav>
  );
};

export default BottomTabBar;
